import * as Actions from 'actions'
import { MIN_TRANSITION_DURATION } from 'constant'
import { maxBy, minBy, partition, throttle } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useDrop } from 'react-dnd'
import { useDispatch, useSelector } from 'react-redux'
import { DRAGNDROP_TYPE, TRANSITIONS, TRANSITION_MOUNT_TYPE } from '~/enums'
import Asset from '~/models/Asset'
import * as Selectors from '~/selectors'
import { pixel2Time, time2Pixel } from '~/Util'
import { getTwoSideTransitionInsertStatus } from '~/helpers/assets/getTwoSideTransitionInsertStatus'
import { isCrossingTransition } from '~/helpers/assets/isCrossingTransition'

const TRANSITION_PLACEHOLDER_OFFSET = 8

function getNeighborAssets(assets, asset) {
  if (asset === undefined) {
    return { right: null, left: null }
  }
  const transitionCapableLayerAssets = Asset.getLayerAssets(assets, asset.layerId)
    .filter(layerAsset => layerAsset.canHaveTransition)
  const getStartTime = x => x.startTime
  const [ left, right ] = partition(
    transitionCapableLayerAssets.filter(el => el.id !== asset.id), x => (
      getStartTime(x) < getStartTime(asset)
    )
  )
  return { left: maxBy(left, getStartTime), right: minBy(right, getStartTime) }
}

function getStrictNeighborAssets(assets, asset) {
  if (asset === undefined) {
    return { right: null, left: null }
  }

  const { left, right } = getNeighborAssets(assets, asset)
  return {
    left: left?.endTime === asset.startTime ? left : null,
    right: right?.startTime === asset.endTime ? right : null,
  }
}

function useDropTransitionItemHandler(assetRef, targetAsset, scale) {
  const assetId = targetAsset.id
  const dispatch = useDispatch()
  const assets = useSelector(Selectors.getAssets)
  const transitions = useSelector(state => Selectors.getTransitionAssets(state))
  const asset = assets.find(el => el.id === assetId)
  const { left, right } = getStrictNeighborAssets(assets, asset)

  return (dropItem, clientOffset) => {
    // drop of transition won't happen if cursor is not in start or
    // end of a media clip, so time < DELTA means start of the clip
    // otherwise we are in the end
    const assetRect = assetRef.current.getBoundingClientRect()
    const time = pixel2Time(clientOffset.x - assetRect.x, scale)

    const midTime = asset.duration / 2
    const isFirstHalf = time < midTime

    let leftVideoAssetId = null
    let rightVideoAssetId = null

    if (dropItem.mountType === TRANSITION_MOUNT_TYPE.BETWEEN) {
      if (isFirstHalf && left) {
        leftVideoAssetId = left.id
        rightVideoAssetId = assetId
      }
      if (!isFirstHalf && right) {
        rightVideoAssetId = right.id
        leftVideoAssetId = assetId
      }
    } else if (dropItem.mountType === TRANSITION_MOUNT_TYPE.IN) {
      rightVideoAssetId = assetId
    } else if (dropItem.mountType === TRANSITION_MOUNT_TYPE.OUT) {
      leftVideoAssetId = assetId
    }

    const { layerId } = asset
    switch (dropItem.type) {
      case DRAGNDROP_TYPE.TRANSITION_ITEM: {
        dispatch(Actions.timeline.createNewTransitionForAsset(
          dropItem,
          leftVideoAssetId,
          rightVideoAssetId,
          { layerId }
        ))
        break
      }
      case DRAGNDROP_TYPE.LAYER_TRANSITION_ASSET: {
        const transition = transitions.find(tr => tr.id === dropItem.id)
        dispatch(Actions.timeline.reattachTransition(
          transition.id,
          leftVideoAssetId,
          rightVideoAssetId,
          transition.leftVideoAssetId,
          transition.rightVideoAssetId,
          dropItem.mountType,
          transition.mountType
        ))
        break
      }
      default:
        break
    }
  }
}

function useTransitionPlaceholderPosition(assetRef, asset, scale) {
  const assets = useSelector(Selectors.getAssets)
  const { left, right } = getStrictNeighborAssets(assets, asset)
  const transitions = useSelector(state => Selectors.getTransitionAssets(state))

  // mouse over asset transitions
  const leftTransitionOfAsset = transitions.find(tr => tr.isAttachedToRight(asset.id))
  const rightTransitionOfAsset = transitions.find(tr => tr.isAttachedToLeft(asset.id))

  // neighbor (no mouse over) Assets transitions
  const rightTransitionOfLeftNeighborAsset = left
    ? transitions.find(tr => tr.isAttachedToLeft(left.id))
    : undefined
  const leftTransitionOfLeftNeighborAsset = left
    ? transitions.find(tr => tr.isAttachedToRight(left.id))
    : undefined
  const leftTransitionOfRightNeighborAsset = right
    ? transitions.find(tr => tr.isAttachedToRight(right.id))
    : undefined
  const rightTransitionOfRightNeighborAsset = right
    ? transitions.find(tr => tr.isAttachedToLeft(right.id))
    : undefined

  return (item, clientOffset) => {
    const result = { placeholderPosition: 0, modifiedDuration: 0, mountType: null }
    // current asset already has 2 transition
    if (leftTransitionOfAsset && rightTransitionOfAsset) {
      return result
    }

    const assetRect = assetRef.current.getBoundingClientRect()

    let transitionType
    switch (item.type) {
      case DRAGNDROP_TYPE.TRANSITION_ITEM:
        transitionType = item.transitionType
        break
      case DRAGNDROP_TYPE.LAYER_TRANSITION_ASSET: {
        // eslint-disable-next-line no-shadow
        const transition = assets.find(el => el.id === item.id)
        transitionType = transition.type
        break
      }
      default:
        break
    }
    const existsAtachedTrans = transitions.find(tr => tr.isAttachedToRight(asset.id))
    const offset = existsAtachedTrans ? time2Pixel(existsAtachedTrans.duration) : 0
    const time = pixel2Time(clientOffset.x - assetRect.x - offset, scale)
    const midTime = asset.duration / 2
    // this is the first or second half of the layer asset that the mouse is hovering over
    const isFirstHalf = time < midTime
    // drag in start of media
    const applyToStart = isFirstHalf
    // drag in end of media
    const applyToEnd = !isFirstHalf


    const isBetween = ((right?.startTime === asset.endTime) && applyToEnd)
      || (applyToStart && (left?.endTime === asset.startTime))

    // any other transitions except fadein and fadeout will have similar behaviour
    if (isCrossingTransition(transitionType) && isBetween) {
      if (!left && !right) {
        return result
      }
      if ((!left && right) || (right && applyToEnd)) {
        if (right.startTime !== asset.endTime
          || rightTransitionOfAsset
          || leftTransitionOfRightNeighborAsset
        ) {
          return result
        }
        const leftExpandDuration = asset.mediaFileDuration
          - asset.duration - asset.mediaStart
        const maxLeftAssetEndTime = asset.startTime + asset.duration + leftExpandDuration
        const minTransitionDuration = Math.min(
          item.duration,
          Math.min(right.endTime - (rightTransitionOfRightNeighborAsset?.duration ?? 0),
            maxLeftAssetEndTime) - asset.startTime,
          right.endTime - Math.max(asset.startTime + (leftTransitionOfAsset?.duration ?? 0),
            right.startTime - right.mediaStart)
        )

        if (minTransitionDuration < MIN_TRANSITION_DURATION) {
          return result
        }

        const status = getTwoSideTransitionInsertStatus(assets, asset,
          right, item, minTransitionDuration)
        if (status) {
          return {
            mountType: TRANSITION_MOUNT_TYPE.BETWEEN,
            modifiedDuration: minTransitionDuration,
            placeholderPosition: assetRect.width - TRANSITION_PLACEHOLDER_OFFSET,
          }
        }
        return {
          ...result,
          mountType: TRANSITION_MOUNT_TYPE.BETWEEN,
          placeholderPosition: assetRect.width - TRANSITION_PLACEHOLDER_OFFSET,
        }
      }

      if ((left && !right) || (left && applyToStart)) {
        if (left.endTime !== asset.startTime
          || leftTransitionOfAsset
          || rightTransitionOfLeftNeighborAsset
        ) {
          return result
        }
        const leftExpandDuration = left.mediaFileDuration
          - left.duration - left.mediaStart
        const maxLeftAssetEndTime = left.startTime + left.duration + leftExpandDuration
        const minTransitionDuration = Math.min(
          item.duration,
          Math.min(asset.endTime - (rightTransitionOfAsset?.duration ?? 0), maxLeftAssetEndTime)
            - left.startTime,
          asset.endTime - Math.max(left.startTime + (
            leftTransitionOfLeftNeighborAsset?.duration ?? 0
          ), asset.startTime - asset.mediaStart)
        )

        if (minTransitionDuration < MIN_TRANSITION_DURATION) {
          return result
        }
        const status = getTwoSideTransitionInsertStatus(assets, left,
          asset, item, minTransitionDuration)

        if (status) {
          return {
            mountType: TRANSITION_MOUNT_TYPE.BETWEEN,
            modifiedDuration: minTransitionDuration,
            placeholderPosition: TRANSITION_PLACEHOLDER_OFFSET * (-1),
          }
        }
        return {
          ...result,
          mountType: TRANSITION_MOUNT_TYPE.BETWEEN,
          placeholderPosition: TRANSITION_PLACEHOLDER_OFFSET * (-1),
        }
      }

      // transition can be applied to the start only if there is no another transition, left video
      // asset doesn't have transition at the end and there is some video asset at left
      if (applyToStart
        && (leftTransitionOfAsset || rightTransitionOfLeftNeighborAsset || left === undefined)
      ) {
        return result
      }

      // transition can be applied to the end only if there is no another transition, right video
      // asset doesn't have transition at the beginning and there is some video asset at right
      if (applyToEnd
        && (rightTransitionOfAsset || leftTransitionOfRightNeighborAsset || right === undefined)
      ) {
        return result
      }
    }

    if (applyToEnd && !isBetween) {
      // fadeout transition can be applied in the end of the clip if there is no another transition
      if (rightTransitionOfAsset) {
        return result
      }
      // can't apply fadeout, if right video asset has transition in the end other than fadein
      if (leftTransitionOfRightNeighborAsset
        && leftTransitionOfRightNeighborAsset.type !== TRANSITIONS.FADEIN
      ) {
        return result
      }
      const minTransDuration = ((asset.duration - (leftTransitionOfAsset?.duration ?? 0)))
      if (minTransDuration < item.duration) {
        if (minTransDuration < MIN_TRANSITION_DURATION) {
          return result
        }
        return {
          mountType: TRANSITION_MOUNT_TYPE.OUT,
          modifiedDuration: minTransDuration,
          placeholderPosition: assetRect.width - TRANSITION_PLACEHOLDER_OFFSET,
        }
      }
      return {
        ...result,
        mountType: TRANSITION_MOUNT_TYPE.OUT,
        placeholderPosition: assetRect.width - TRANSITION_PLACEHOLDER_OFFSET,
      }
    }

    if (applyToStart && !isBetween) {
      // fadein transition can be applied in the start of the clip if there is no another transition
      if (leftTransitionOfAsset) {
        return result
      }
      // can't apply fadein, if left video asset has transition in the end other than fadeout
      if (rightTransitionOfLeftNeighborAsset
        && rightTransitionOfLeftNeighborAsset.type !== TRANSITIONS.FADEOUT
      ) {
        return result
      }
      const minTransDuration = ((asset.duration - (rightTransitionOfAsset?.duration ?? 0)))
      if (minTransDuration < item.duration) {
        if (minTransDuration < MIN_TRANSITION_DURATION) {
          return result
        }
        return {
          mountType: TRANSITION_MOUNT_TYPE.IN,
          modifiedDuration: minTransDuration,
          placeholderPosition: TRANSITION_PLACEHOLDER_OFFSET * (-1),
        }
      }
      return {
        ...result,
        mountType: TRANSITION_MOUNT_TYPE.IN,
        placeholderPosition: TRANSITION_PLACEHOLDER_OFFSET * (-1),
      }
    }

    if (applyToStart && !isCrossingTransition(transitionType)) {
      return {
        ...result,
        mountType: TRANSITION_MOUNT_TYPE.IN,
        placeholderPosition: TRANSITION_PLACEHOLDER_OFFSET * (-1),
      }
    }

    if (applyToEnd && !isCrossingTransition(transitionType)) {
      return {
        ...result,
        mountType: TRANSITION_MOUNT_TYPE.OUT,
        placeholderPosition: assetRect.width - TRANSITION_PLACEHOLDER_OFFSET,
      }
    }

    return result
  }
}

export default function useAssetDropTarget(asset, assetRef) {
  const scale = useSelector(state => state.timeline.scale)
  const onDropTransitionItem = useDropTransitionItemHandler(assetRef, asset, scale)
  const getTransitionPlaceholderPosition = useTransitionPlaceholderPosition(assetRef, asset, scale)
  const transitionPlaceholderPosition = useMemo(
    () => throttle(getTransitionPlaceholderPosition, 200),
    [ getTransitionPlaceholderPosition ]
  )

  const [ placeholderPosition, setPlaceholderPosition ] = useState(0)
  const [ modifiedDuration, setModifiedDuration ] = useState(0)
  const [ mountType, setMountType ] = useState(null)

  const [{ isDragOver }, drop ] = useDrop({
    accept: [
      DRAGNDROP_TYPE.TRANSITION_ITEM,
      DRAGNDROP_TYPE.LAYER_TRANSITION_ASSET,
    ],
    collect: monitor => ({
      isDragOver: monitor.isOver(),
    }),
    drop(item, monitor) {
      // if cursor is not near the end or start of the clip
      // don't allow to drop transition
      if (monitor.didDrop() || placeholderPosition === 0) {
        return
      }

      const clientOffset = monitor.getClientOffset()
      onDropTransitionItem({ ...item, modifiedDuration, mountType }, clientOffset)
    },
    hover(item, monitor) {
      const clientOffset = monitor.getClientOffset()
      const { placeholderPosition,
        modifiedDuration, mountType } = transitionPlaceholderPosition(item, clientOffset)
      setPlaceholderPosition(placeholderPosition)
      setModifiedDuration(modifiedDuration)
      setMountType(mountType)
    },
  })

  useEffect(() => {
    if (!isDragOver) {
      setPlaceholderPosition(0)
    }
  }, [ isDragOver ])

  drop(assetRef)

  return { isDragOver, placeholderPosition }
}
